import { Component } from 'react';
import 'iframe-resizer/js/iframeResizer.contentWindow.min';
import styled from '@emotion/styled';
import Slider from 'react-slick';

// Components
import WidgetCounter from './Counter';
import WidgetHeader from './Header';
import Nav from './Nav';
import WidgetSlide from './Slide';
import { imageOverlayConfig } from '../../../Roofing/DesignAndInspire/utils/imageOverlayConfig';

const WidgetContainer = styled.div`
  background-color: #ffffff;
  display: inline-block;
  left: 50%;
  max-width: 1200px;
  position: relative;
  transform: translateX(-50%);
  width: 100%;
  font-family: 'akzidenz-grotesk', sans-serif;
`;

const SliderHeading = styled.h2`
  display: inline-block;
  margin: 0 5% 2rem;
  position: relative;
  text-align: center;
  width: 90%;
  color: #666;
  font-family: 'akzidenz-grotesk', sans-serif;
  font-weight: 300;

  font-size: 30px;
  line-height: 34px;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    font-size: 21px;
    line-height: 23px;
  }
`;

const SliderContainer = styled(Slider)`
  display: inline-block;
  padding: 0 1em;
  position: relative;
  width: 100%;
`;

const SliderArrow = styled.button`
  font-size: 1em;
  height: 4em;
  left: ${props => (props.direction == 'left' ? '1em' : 'auto')};
  position: absolute;
  right: ${props => (props.direction == 'right' ? '1em' : 'auto')};
  top: 50%;
  transform: translateY(-50%);
  width: 3em;
  z-index: 1;

  @media (max-width: 768px) {
    top: 35%;
  }

  &:before {
    color: #000000;
    content: ${props => props.content};
    display: inline-block;
    font-size: 3em;
    height: 100%;
    left: ${props => (props.direction == 'left' ? '0.2em' : 'auto')};
    position: absolute;
    right: ${props => (props.direction == 'right' ? '-0.7em' : 'auto')};
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
  }
`;

const NavContainer = styled(Slider)`
  margin: 0 auto;
  max-width: 600px;
  position: relative;
  width: 100%;

  &:after {
    box-shadow: inset -4em 0 2.5em -1.5em #ffffff;
    content: '';
    display: inline-block;
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const SelectLanguage = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid #959595;
  border-bottom: 1px solid #959595;
  background: #e6e6e6;
  gap: 16px;
  padding: 4px;

  > select {
    padding: 4px 16px 4px 4px;
  }
`;

const TRANSLATIONS = {
  'en-US': {
    title: 'Shingle and Color Pairings',
    description: 'Browse and select your shingle and color pairing.',
    label: 'Select Language',
    of: 'of',
  },
  'es-US': {
    title: 'Pares de tejas y colores',
    description: 'Explore y seleccione su combinación de tejas y colores.',
    label: 'Seleccionar idioma',
    of: 'de',
  },
};

const lazyLoadType = 'progressive';

export default class Page extends Component {
  state = {
    currentSlide: 0,
    loading: true,
    mainSlider: null,
    navSlider: null,
    visibility: 'hidden',
    slideDelay: 4000,
    styleboards: [],
    widgetLanguage: navigator.language || 'en-US',
  };

  static async getInitialProps() {
    return {
      digitalData: {
        global: {
          siteName: 'widget js',
          widgetName: 'Design and Inspire Widget',
          pageName: 'oc | widget | roofing | design-and-inspire',
          pageSection: ['Widget', 'Roofing', 'Design and Inspire'],
        },
      },
    };
  }

  processStyleboardsFromApi(styleboards) {
    return styleboards.map((styleboard, index) => {
      const { heroImage } = styleboard.contents[0];
      const shingleColor = styleboard.contents[0].shingleColor.color.__data.data.shingle_colors[0];
      const { baseLayer, overlays } = imageOverlayConfig({
        shingleColor,
        heroImage,
        textStyle: 'OoohBaby.ttf',
      });

      return {
        baseLayer,
        overlays,
        id: index,
        title: `${styleboard.contents[0].colorName} & ${styleboard.contents[0].shingleColor.color.__data.data.shingle_colors[0].name} ${styleboard.contents[0].shingleColor.color.__data.data.full_proper_name}`,
      };
    });
  }

  updateWidgetLanguage = e => {
    if (e.key === 'widgetDefaultLanguage' && e.newValue) {
      this.setState({
        widgetLanguage: e.newValue,
      });
    }
  };

  async componentDidMount() {
    const action = this.props.dataEndpoint;
    const requestOptions = {
      method: 'get',
      headers: { 'Content-Type': 'application/json' },
    };
    fetch(action, requestOptions)
      .then(response => response.json())
      .then(json => {
        console.log({ json });
        this.setState({
          styleboards: this.processStyleboardsFromApi(json.data),
          loading: false,
          mainSlider: this.mainSlider,
          navSlider: this.navSlider,
          visibility: 'visible',
          fullData: json.data,
        });
      });

    // Get widget default language if present
    if (window.localStorage.getItem('widgetDefaultLanguage')) {
      this.setState({
        widgetLanguage: window.localStorage.getItem('widgetDefaultLanguage'),
      });
    } else {
      this.setState({
        widgetLanguage: navigator.language,
      });
    }
    window.addEventListener('storage', this.updateWidgetLanguage);
  }

  async componentWillUnmount() {
    window.removeEventListener('storage', this.updateWidgetLanguage);
  }

  handleLanguageChange(e) {
    this.setState({
      widgetLanguage: e.target.value,
    });
    window.localStorage.setItem('widgetDefaultLanguage', e.target.value);
  }

  render() {
    const mainSliderSettings = {
      arrows: true,
      autoplay: true,
      autoplaySpeed: this.state.slideDelay,
      beforeChange: (current, next) => this.setState({ currentSlide: next }),
      dots: false,
      draggable: true,
      infinite: true,
      lazyLoad: lazyLoadType,
      nextArrow: <SliderArrow content="'\232a'" direction="right" />,
      pauseOnHover: true,
      prevArrow: <SliderArrow content="'\2329'" direction="left" />,
    };

    const navSliderSettings = {
      arrows: false,
      dots: false,
      infinite: true,
      lazyLoad: lazyLoadType,
      slidesToShow: 4,
      swipeToSlide: true,
    };

    return (
      <main>
        <SelectLanguage>
          <label htmlFor="widget-language">
            <b>{TRANSLATIONS[this.state.widgetLanguage].label}</b>
          </label>
          <select id="widget-language" value={this.state.widgetLanguage} onChange={e => this.handleLanguageChange(e)}>
            <option value="en-US">English</option>
            <option value="es-US">Spanish</option>
          </select>
        </SelectLanguage>
        <WidgetContainer className={this.state.visibility}>
          <WidgetHeader title={TRANSLATIONS[this.state.widgetLanguage].title} />

          <SliderHeading>{TRANSLATIONS[this.state.widgetLanguage].description}</SliderHeading>

          <SliderContainer
            asNavFor={this.state.navSlider}
            ref={slider => (this.mainSlider = slider)}
            {...mainSliderSettings}
          >
            {this.state.styleboards.map(styleboard => (
              <WidgetSlide
                key={styleboard.id}
                title={styleboard.title}
                baseLayer={styleboard.baseLayer}
                overlays={styleboard.overlays}
                tracking={{
                  'data-track': 'learn-more',
                  'data-track-element-location': 'color-pairing-selection',
                }}
              />
            ))}
          </SliderContainer>

          <WidgetCounter
            current={this.state.currentSlide + 1}
            total={this.state.styleboards.length}
            of={TRANSLATIONS[this.state.widgetLanguage].of}
          />

          <NavContainer ref={slider => (this.navSlider = slider)} {...navSliderSettings}>
            {this.state.styleboards.map(styleboard => (
              <Nav
                key={styleboard.id}
                id={styleboard.id}
                background={styleboard.baseImages}
                currentSlide={this.state.currentSlide}
                onClick={e => this.mainSlider.slickGoTo(styleboard.id)}
                slideIndex={this.props.slideIndex}
                baseLayer={styleboard.baseLayer}
                overlays={styleboard.overlays}
              />
            ))}
          </NavContainer>
        </WidgetContainer>
      </main>
    );
  }
}
